/* eslint-disable react/jsx-no-useless-fragment */
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { getDocuments } from 'store/modules/documents_store/actions';
import { DocumentData, DocumentsState } from 'store/modules/documents_store/types';
import SelectButton from 'components/atoms/select_button';
import _ from 'underscore';
import Loader from 'components/atoms/loader';
import { createBiddingManagementAttachment } from 'store/modules/management_store/actions';
import Select from 'react-select';
import { ProfileState } from 'store/modules/profile_store/types';
import { Container } from './styles';

interface ImportDocumentsModalProps {
  importDocumentsModalOpened: boolean;
  setImportDocumentsModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  biddingId: number;
}

interface UploadProgressTypes {
  [key: string]: number;
}

const ImportDocumentsModal = (props: ImportDocumentsModalProps) => {
  const { importDocumentsModalOpened, setImportDocumentsModalOpened, biddingId } = props;
  const [loading, setLoading] = useState(false);

  const { documents, request, documents_list } = useSelector<ApplicationState, DocumentsState>(
    (state) => state.session.documents,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [selectedDocuments, setSelectedDocuments] = useState<Array<number>>([]);
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);
  const [documentList, setDocumentList] = useState<Array<DocumentData>>([]);
  const [monitoredCompaniesOptions, setMonitoredCompaniesOptions] = useState<Array<any>>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressTypes>({});
  const [monitoredCompaniesSelected, setMonitoredCompaniesSelected] = useState<Array<any> | null>(
    null,
  );

  const updateUploadProgress = (fileName: string, progress: number) => {
    setUploadProgress({
      ...uploadProgress,
      [`${fileName}`]: progress,
    });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      getDocuments({
        cnpj: (monitoredCompaniesSelected && monitoredCompaniesSelected[0]?.value) || profile?.cnpj,
      }),
    );
  }, [monitoredCompaniesSelected && monitoredCompaniesSelected[0]?.value]);

  useEffect(() => {
    setDocumentList(
      documents.filter((documents) =>
        ['Disponível', 'Aguardando Inscrição'].includes(documents.statusLabel),
      ),
    );
  }, [documents]);

  const situationBadge = (document: DocumentData) => {
    return (
      <span
        className={`badge badge-${
          document.statusLabel === 'PendenteInscrição' ? 'secondary' : 'success'
        }`}
      >
        {document.statusLabel}
      </span>
    );
  };

  const importDocuments = async () => {
    setLoading(true);
    try {
      const promises = selectedDocuments.map((selectedDocument) => {
        const formData = new FormData();
        formData.append('biddingId', biddingId.toString());
        formData.append('referer', 'documents');
        formData.append('refererId', documentList[selectedDocument - 1].id.toString());
        formData.append('description', documentList[selectedDocument - 1].name || '');
        formData.append('fileName', documentList[selectedDocument - 1].name || '');
        formData.append('fileUrl', documentList[selectedDocument - 1].fileUrl || '');

        return dispatch(
          createBiddingManagementAttachment(
            formData,
            documentList[selectedDocument - 1].name,
            biddingId.toString(),
            updateUploadProgress,
          ),
        );
      });

      await Promise.all(promises);
      setImportDocumentsModalOpened(false);
    } catch (error) {
      console.error('Erro ao importar documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectedDocuments = (id: number, updateAll = false) => {
    let selectedIds: Array<number> = [];

    if (updateAll) {
      if (wholePageSelected) {
        const documentIdList = documentList.map((document, index) => index + 1);
        selectedIds = selectedDocuments.filter(
          (selectedDocument) => !documentIdList.includes(selectedDocument),
        );
      }
      if (!wholePageSelected)
        selectedIds = [
          ...selectedDocuments,
          ...(documentList.map((document, index) => index + 1) || []),
        ];
    }
    if (!updateAll) {
      if (selectedDocuments.includes(id))
        selectedIds = selectedDocuments.filter((selectedDocument) => selectedDocument !== id);
      if (!selectedDocuments.includes(id)) selectedIds = [...selectedDocuments, id];
    }

    setSelectedDocuments(_.uniq(selectedIds));
  };

  useEffect(() => {
    if (documentList.length === 0) return;

    if (selectedDocuments.length === documentList.length) {
      setWholePageSelected(true);
    } else {
      setWholePageSelected(false);
    }
  }, [selectedDocuments]);

  useEffect(() => {
    setMonitoredCompaniesOptions(
      documents_list?.monitored_companies.map((companie) => {
        return {
          value: companie?.monitored_company,
          label: (
            <>
              {companie.company_name}
              <br />
              <small>
                {companie.monitored_company.replace(
                  /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                  '$1.$2.$3/$4-$5',
                )}
              </small>
            </>
          ),
          companyName: companie.company_name,
        };
      }),
    );
  }, [documents_list?.monitored_companies.length]);
  return (
    <Modal size="lg" show={importDocumentsModalOpened}>
      <Container>
        <div className="modal-content">
          <div className="modal-header">
            <h6 className="modal-title">Meus Documentos</h6>
            <button
              type="button"
              className="close"
              onClick={() => setImportDocumentsModalOpened(false)}
            >
              x
            </button>
          </div>
          <div className="modal-body">
            {request?.get_documents?.loading && <Loader />}
            {request?.get_documents?.loaded && (
              <>
                <Select
                  name="monitoredCompanies"
                  value={monitoredCompaniesSelected || monitoredCompaniesOptions[0]}
                  closeMenuOnSelect
                  onChange={(event) => setMonitoredCompaniesSelected([event])}
                  options={monitoredCompaniesOptions}
                  className="basic-multi-select mb-4"
                />
                {documentList.length === 0 ? (
                  <div className="text-center">
                    <strong>Nenhum documento encontrado.</strong>
                  </div>
                ) : (
                  <>
                    <div className="text-center pb-2">
                      <strong>{selectedDocuments.length}</strong>{' '}
                      {selectedDocuments.length !== 1
                        ? 'documentos selecionados'
                        : 'documento selecionado'}
                    </div>
                    <div className="mt-0 mb-1">
                      <small className="text-danger">
                        * Documentos com a situação{' '}
                        <span className="py-1 px-2 badge badge-warning">Indisponível</span> não
                        serão listados.
                      </small>
                    </div>
                    <div className="col-md-12">
                      <div className="row documents-header py-3 mt-3">
                        <div className="col-md-1">
                          <SelectButton
                            id={0}
                            isChecked={wholePageSelected}
                            text=""
                            toggleSelected={toggleSelectedDocuments}
                          />
                        </div>
                        <div className="col-md-4 text-truncate">Documentos</div>
                        <div className="col-md-2">Grupo</div>
                        <div className="col-md-2">Situação</div>
                        <div className="col-md-2">Vencimento</div>
                        <div className="col-md-1">Ações</div>
                      </div>
                      {documentList.map((document, index) => {
                        const isExpired =
                          moment(document.expirationDate).isValid() &&
                          moment(document.expirationDate).isBefore(moment(), 'day');

                        return (
                          <div
                            className={`row border-bottom align-items-center py-3 ${
                              isExpired ? 'alert-danger text' : ''
                            }`}
                          >
                            <div className="col-md-1">
                              <SelectButton
                                id={index + 1}
                                text=""
                                isChecked={selectedDocuments.includes(index + 1)}
                                toggleSelected={toggleSelectedDocuments}
                              />
                            </div>

                            <div className="col-md-4 text-truncate" title={document.name}>
                              <p className="mb-0">{document.name}</p>
                              <small>{document?.description}</small>
                            </div>
                            <div className="col-md-2">{document.document_group}</div>
                            <div className="col-md-2">
                              {isExpired ? (
                                <span className="badge badge-danger">Pendente</span>
                              ) : (
                                situationBadge(document)
                              )}
                            </div>
                            <div className="col-md-2 text-center">
                              {moment(document.expirationDate).isValid()
                                ? moment(document.expirationDate).format('L')
                                : '-'}
                            </div>
                            <div className="col-md-1">
                              <div className="d-flex">
                                <a
                                  className="btn btn-link btn-sm mr-1 p-0 d-flex align-items-center"
                                  href={`${document.fileUrl}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <span className="material-icons">description</span>
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                <div className="text-center mt-3">
                  <button
                    className="btn btn-sm btn-primary"
                    type="button"
                    disabled={selectedDocuments.length === 0 || loading}
                    onClick={() => importDocuments()}
                  >
                    Importar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Container>
    </Modal>
  );
};

export default ImportDocumentsModal;
